import { createClient } from '@supabase/supabase-js'

// Debug environment variables
console.log('🔍 Environment Debug:', {
  NODE_ENV: import.meta.env.MODE,
  PROD: import.meta.env.PROD,
  DEV: import.meta.env.DEV,
  VITE_SUPABASE_URL: import.meta.env.VITE_SUPABASE_URL ? '✅ Present' : '❌ Missing',
  VITE_SUPABASE_ANON_KEY: import.meta.env.VITE_SUPABASE_ANON_KEY ? '✅ Present' : '❌ Missing',
  allEnvKeys: Object.keys(import.meta.env).filter(key => key.startsWith('VITE_'))
});

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

// Fallback values for debugging (remove in production)
const fallbackUrl = 'https://wjyejyzpdmjbyxekvajs.supabase.co'
const fallbackKey = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6IndqeWVqeXpwZG1qYnl4ZWt2YWpzIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDg0ODA3NDQsImV4cCI6MjA2NDA1Njc0NH0.zDOqu1_3fsApiOrOCR5Enh8OmjXAmlGQRPrrNaNhjs8'

// Use environment variables if available, otherwise use fallbacks temporarily
const finalUrl = supabaseUrl || fallbackUrl
const finalKey = supabaseAnonKey || fallbackKey

console.log('🔧 Supabase Config:', {
  usingFallback: !supabaseUrl || !supabaseAnonKey,
  hasUrl: !!finalUrl,
  hasKey: !!finalKey
});

// Create supabase client
export const supabase = createClient(finalUrl, finalKey)

// Helper function to check if Supabase is configured
export const isSupabaseConfigured = () => !!(finalUrl && finalKey)

// Type for waitlist entry
export interface WaitlistEntry {
  id?: number
  email: string
  created_at?: string
}

// Function to add email to waitlist
export async function addToWaitlist(email: string) {
  if (!supabase) {
    // More user-friendly error for production
    if (import.meta.env.PROD) {
      throw new Error('Service temporarily unavailable. Please try again later.')
    } else {
      throw new Error('Supabase is not configured. Please add VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY to your environment variables.')
    }
  }

  try {
    const { data, error } = await supabase
      .from('waitlist_entries')
      .insert([{ email }]);

    if (error) {
      // Handle specific error types
      if (error.code === '23505') {
        // Unique constraint violation (duplicate email)
        const duplicateError = new Error('This email is already on the waitlist.');
        (duplicateError as any).code = 'DUPLICATE_EMAIL';
        throw duplicateError;
      } else if (error.code === '42P01') {
        // Table doesn't exist
        throw new Error('Waitlist service is not properly configured.')
      } else if (error.message?.includes('JWT')) {
        // Authentication issues
        throw new Error('Authentication error. Please try again.')
      } else {
        // Generic error
        console.error('Supabase error:', error);
        throw new Error('Failed to join waitlist. Please try again.')
      }
    }

    return data
  } catch (error) {
    // Re-throw our custom errors, or wrap unknown errors
    if (error instanceof Error) {
      throw error
    } else {
      throw new Error('An unexpected error occurred. Please try again.')
    }
  }
}